import { Fragment } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChartNoAxesColumnIncreasing, SquarePlus, Send, XIcon } from 'lucide-react';
import { Box } from '@mui/material';
import { RightPanelOverlayModal } from 'app/shared-components/modals';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import welcomeIllustration from '../assets/welcome-illustration.svg';

type Feature = {
  icon: LucideIcon;
  title: string;
  description: string;
};

const features: Feature[] = [
  {
    icon: ChartNoAxesColumnIncreasing,
    title: 'Track',
    description: 'Your revenue and performance',
  },
  {
    icon: SquarePlus,
    title: 'Upgrade',
    description: "Unlock your pharmacy's potential",
  },
  {
    icon: Send,
    title: 'Learn',
    description: 'How to make the most of your Healthera partnership',
  },
];

type WelcomeModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function WelcomeModal({ open, onClose }: WelcomeModalProps) {
  return (
    <RightPanelOverlayModal open={open} onClose={onClose}>
      <Box className="vtwo:w-full vtwo:!max-w-[720px] vtwo:max-h-full vtwo:overflow-auto vtwo:rounded-xl vtwo:border vtwo:bg-white vtwo:shadow-lg vtwo:outline-none">
        <div className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:px-6 vtwo:py-4 vtwo:sticky vtwo:top-0 vtwo:bg-white">
          <h3 className="vtwo:text-base vtwo:font-semibold vtwo:leading-6 vtwo:text-gray-900">
            Command Centre
          </h3>
          <Button variant="ghost" size="icon" className="vtwo:size-8" onClick={onClose}>
            <XIcon className="vtwo:size-4" />
            <span className="vtwo:sr-only">Close</span>
          </Button>
        </div>

        <div className="vtwo:flex vtwo:flex-col vtwo:gap-10 vtwo:px-6 vtwo:pt-5 vtwo:pb-5">
          <div className="vtwo:flex vtwo:flex-col vtwo:items-center vtwo:gap-3">
            <img src={welcomeIllustration} alt="Welcome illustration" className="vtwo:w-[85px]" />
            <h2 className="vtwo:text-2xl vtwo:font-semibold vtwo:leading-8 vtwo:text-gray-900 vtwo:text-center">
              Welcome to the new Command Centre
            </h2>
            <p className="vtwo:text-base vtwo:font-normal vtwo:leading-6 vtwo:text-gray-500 vtwo:text-center">
              The command centre gives you a one stop shop for the most important and urgent
              information on your Connect platform.
            </p>
          </div>

          <div className="vtwo:flex vtwo:items-stretch vtwo:gap-5">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Fragment key={feature.title}>
                  <div className="vtwo:flex vtwo:flex-1 vtwo:flex-col vtwo:items-center vtwo:gap-2.5">
                    <Icon className="vtwo:size-8" />
                    <span className="vtwo:text-lg vtwo:font-semibold vtwo:leading-7 vtwo:text-gray-900 vtwo:text-center">
                      {feature.title}
                    </span>
                    <span className="vtwo:text-base vtwo:font-normal vtwo:leading-6 vtwo:text-gray-500 vtwo:text-center">
                      {feature.description}
                    </span>
                  </div>
                  {index < features.length - 1 && (
                    <Separator orientation="vertical" className="vtwo:h-auto!" />
                  )}
                </Fragment>
              );
            })}
          </div>

          <div>
            <p className="vtwo:text-base vtwo:font-normal vtwo:leading-6 vtwo:text-gray-500 vtwo:text-center">
              If you have any questions you can book a meeting with your account manager in the top
              right, or leave feedback to help us improve.
            </p>
          </div>
        </div>

        {/* Footer / CTA */}
        <div className="vtwo:flex vtwo:items-center vtwo:justify-center vtwo:px-6 vtwo:pt-3 vtwo:pb-6">
          <Button
            onClick={onClose}
            size="lg"
            className="vtwo:w-64 vtwo:bg-teal-900 vtwo:text-white vtwo:hover:bg-teal-950"
          >
            Let's go!
          </Button>
        </div>
      </Box>
    </RightPanelOverlayModal>
  );
}
